/**
 * <MessageActions /> — the action row underneath each settled
 * assistant turn (Phase 1+2 of the chat feedback feature).
 *
 * Three actions, in order:
 *   1. Copy        — copies the assistant's plain text to the clipboard.
 *   2. Thumbs up   — POSTs sentiment='up' to /api/chat/feedback.
 *   3. Thumbs down — POSTs sentiment='down'; opens an inline popover
 *                    asking "what was wrong with this answer?" before
 *                    sending (the comment is optional — empty submit
 *                    is fine).
 *
 * Hidden while the turn is still streaming. Hidden when the turn has
 * no `text` to copy AND no `sessionId` to attach feedback to (e.g.
 * tool-only turns with no synthesis text and no session yet).
 *
 * Bilingual + RTL-safe via tailwind logical properties (gap, ms-/me-).
 */

import { Check, Copy, ThumbsDown, ThumbsUp, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { api } from "../../lib/api";

export interface MessageActionsProps {
  /** Client-side stable id for this assistant turn. */
  turnId: string;
  /** The ephemeral chat session id (created by the orchestrator on
   *  the first SSE event). Feedback is stored against this. */
  sessionId: string | null;
  /** The plain-text assistant text — copied as-is on Copy. Also
   *  snapshotted into the feedback row so admins see context
   *  without joining transcripts. */
  text: string;
}

type Sentiment = "up" | "down";

export function MessageActions({
  turnId,
  sessionId,
  text,
}: MessageActionsProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  // Local "this user clicked thumbs-X on this turn" state. Optimistic:
  // we flip it as soon as the user clicks, before the POST lands.
  const [sentiment, setSentiment] = useState<Sentiment | null>(null);
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState("");
  const [submittingComment, setSubmittingComment] = useState(false);
  const commentRef = useRef<HTMLTextAreaElement | null>(null);

  // 1.5s "Copied" toast.
  useEffect(() => {
    if (!copied) return;
    const id = setTimeout(() => setCopied(false), 1500);
    return () => clearTimeout(id);
  }, [copied]);

  // Autofocus the comment box when it opens.
  useEffect(() => {
    if (showComment) {
      requestAnimationFrame(() => commentRef.current?.focus());
    }
  }, [showComment]);

  async function onCopy() {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
    } catch {
      // navigator.clipboard can throw in non-secure contexts / older
      // browsers — fail silently. Showing an error toast would be
      // worse than letting the user select+copy manually.
    }
  }

  async function postFeedback(
    next: Sentiment,
    optComment?: string,
  ): Promise<void> {
    if (!sessionId) return;
    try {
      await api.postChatFeedback({
        session_id: sessionId,
        turn_id: turnId,
        sentiment: next,
        comment: optComment?.trim() || null,
        message_snippet: text.slice(0, 500),
      });
    } catch {
      // Feedback is fire-and-forget UX. A failed POST shouldn't
      // surface to the user — it just means the row didn't land,
      // and the next click can replay.
    }
  }

  function onThumbUp() {
    const next: Sentiment = sentiment === "up" ? "up" : "up";
    setSentiment(next);
    setShowComment(false);
    void postFeedback("up");
  }

  function onThumbDown() {
    setSentiment("down");
    setShowComment(true);
    // Send sentiment=down immediately so we capture the click even
    // if the user closes the popover without typing.
    void postFeedback("down");
  }

  function onSubmitComment() {
    if (submittingComment) return;
    setSubmittingComment(true);
    void postFeedback("down", comment).finally(() => {
      setSubmittingComment(false);
      setShowComment(false);
    });
  }

  function onCloseComment() {
    setShowComment(false);
  }

  const baseBtn =
    "inline-flex h-6 items-center gap-1 rounded-md border border-transparent " +
    "px-1.5 text-xs text-text-tertiary transition-colors " +
    "hover:bg-surface-sunken hover:text-text-secondary";

  return (
    <div className="flex flex-col gap-1.5 pt-0.5">
      <div className="flex items-center gap-0.5">
        {/* Copy */}
        <button
          type="button"
          onClick={onCopy}
          aria-label={t("app.chat_action_copy")}
          title={t("app.chat_action_copy")}
          className={baseBtn}
        >
          {copied ? (
            <>
              <Check size={11} aria-hidden />
              <span>{t("app.chat_action_copied")}</span>
            </>
          ) : (
            <Copy size={11} aria-hidden />
          )}
        </button>

        {/* Thumbs up */}
        <button
          type="button"
          onClick={onThumbUp}
          aria-label={t("app.chat_action_thumbs_up")}
          title={t("app.chat_action_thumbs_up")}
          aria-pressed={sentiment === "up"}
          disabled={!sessionId}
          className={[
            baseBtn,
            sentiment === "up"
              ? "text-emerald-700 bg-emerald-50 hover:bg-emerald-100"
              : "",
            !sessionId ? "opacity-40 cursor-not-allowed" : "",
          ].join(" ")}
        >
          <ThumbsUp size={11} aria-hidden />
        </button>

        {/* Thumbs down */}
        <button
          type="button"
          onClick={onThumbDown}
          aria-label={t("app.chat_action_thumbs_down")}
          title={t("app.chat_action_thumbs_down")}
          aria-pressed={sentiment === "down"}
          disabled={!sessionId}
          className={[
            baseBtn,
            sentiment === "down"
              ? "text-rose-700 bg-rose-50 hover:bg-rose-100"
              : "",
            !sessionId ? "opacity-40 cursor-not-allowed" : "",
          ].join(" ")}
        >
          <ThumbsDown size={11} aria-hidden />
        </button>
      </div>

      {/* Comment popover — appears after thumbs-down. */}
      {showComment && (
        <div className="flex flex-col gap-1.5 rounded-md border border-border-subtle bg-surface-default p-2 text-xs">
          <div className="flex items-center justify-between gap-2">
            <span className="font-medium text-text-secondary">
              {t("app.chat_feedback_comment_prompt")}
            </span>
            <button
              type="button"
              onClick={onCloseComment}
              aria-label={t("common.close")}
              className="text-text-tertiary hover:text-text-secondary"
            >
              <X size={11} aria-hidden />
            </button>
          </div>
          <textarea
            ref={commentRef}
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            maxLength={500}
            rows={2}
            placeholder={t("app.chat_feedback_comment_placeholder")}
            className="w-full resize-none rounded border border-border-subtle bg-surface-sunken p-1.5 text-xs text-text-primary outline-none focus:border-brand/40"
          />
          <div className="flex items-center justify-end gap-1.5">
            <button
              type="button"
              onClick={onSubmitComment}
              disabled={submittingComment}
              className="rounded bg-brand px-2 py-0.5 text-xs font-medium text-brand-fg hover:opacity-90 disabled:opacity-50"
            >
              {submittingComment
                ? t("app.chat_feedback_submitting")
                : t("app.chat_feedback_submit")}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
